import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, CircularProgress, IconButton } from '@mui/material';
import { useRef, useState } from 'react';
import PinInput from 'react-pin-input';
import { Alert } from '../../../../shared/lib/common/Alert';
import { PageHeading } from '../../../../shared/lib/common/PageHeading';
import { INVALID_AUTH_TOKEN, LABEL_SECURITY_CODE_PIN, MSG_UPDATE_SECURITY_CODE, BUTTON_TEXT_CHANGE_PIN_CODE, HEADING_SIX_DIGIT_PIN_CODE } from '../../../../shared/lib/constants';
import palette from '../../../../shared/lib/theme/palette';
import { backDropStyle, userDetailsWrapper } from '../../../../shared/lib/theme/styleConstants';
import { MSG_PIN_TOO_SHORT, MSG_PIN_UPDATED, MSG_PLEASE_CHECK_YOUR_PHONE, THERE_WAS_SOME_ERROR_WHILE_SENDING_SMS } from '../../app/constants';
import { useAppContext } from '../../app/contexts/AppContext';
import { ActionType } from '../../app/reducer/AppContextReducer';
import { CustomPinInput } from '../CustomPinInput';
import { VerifyOtp } from '../VerifyOtp';
import { CustomModal } from '../common/CustomModal';
import { OtpVerification, useAddSecurityPinMutation, useSendOtpV2Mutation } from '../../generated';

export const SecurityCode = () => {
  const { borderLight, blackOne, blackFive } = palette;
  const pinRef = useRef<PinInput | null>(null);
  const [tpin, setTpin] = useState<string>('');
  const { dispatch: appDispatch, isSecurityPinEdit, currentUser, setCurrentUser } = useAppContext();
  const [modalStep2, setModalStep2] = useState<boolean>(false);
  const { phoneNumber } = currentUser || {};
  const [infoSteps, setInfoSteps] = useState<boolean>(false);
  const [code, setCode] = useState<string | null | undefined>(undefined);

  const onChange = (value: string) => {
    setTpin(value);
  };

  const [addSecurityPin] = useAddSecurityPinMutation({
    onError({ message }) {
      Alert.error(message);
      setTpin('');
      pinRef.current?.clear();
    },

    onCompleted() {
      if (currentUser) {
        const { status: userStatus, email, emailVerified, isSubscribed, phoneNumber } = currentUser;
        const isTPinAdded = true;
        const updatedUser = { ...currentUser, status: userStatus, email, emailVerified, isSubscribed, isTPinAdded, phoneNumber: phoneNumber?.startsWith('+') ? phoneNumber : `+${phoneNumber}` };
        setCurrentUser(updatedUser);
        Alert.success(MSG_PIN_UPDATED);
        onModalClose();
      }
    }
  });

  const onTpinSubmit = async () => {
    if (tpin.length < 6) {
      Alert.error(MSG_PIN_TOO_SHORT);
    } else {
      tpin &&
        (await addSecurityPin({
          variables: {
            addOrUpdateSecurityPinInput: {
              securityPin: tpin
            }
          }
        }));
    }
  };

  const onModalClose = () => {
    appDispatch({ type: ActionType.SET_IS_SECURITY_PIN_EDIT, isSecurityPinEdit: false });
    setModalStep2(false);
    setInfoSteps(false);
    setTpin('');
    setCode(undefined);
  };

  const [sendOtpV2, { loading }] = useSendOtpV2Mutation({
    onError({ message }) {
      if (message === INVALID_AUTH_TOKEN) Alert.error(INVALID_AUTH_TOKEN);
      else if (message === THERE_WAS_SOME_ERROR_WHILE_SENDING_SMS) Alert.error(THERE_WAS_SOME_ERROR_WHILE_SENDING_SMS);
      else Alert.error(message);
    },

    onCompleted(data) {
      const { sendOtpV2 } = data ?? {};
      const { code } = sendOtpV2 ?? {};
      if (sendOtpV2) {  
        setCode(code);
        Alert.success(MSG_PLEASE_CHECK_YOUR_PHONE);
        appDispatch({ type: ActionType.SET_TIME_LEFT, timeLeft: 60 });
        setInfoSteps(true);
        appDispatch({ type: ActionType.SET_IS_SECURITY_PIN_EDIT, isSecurityPinEdit: true });
      }
    }
  });

  const onButtonClick = async () => {
    setModalStep2(false);
    setInfoSteps(false);

    phoneNumber &&
      (await sendOtpV2({
        variables: {
          sendOtpInputV2: {
            phoneNumber,
            otpType: OtpVerification.TPinVerify
          }
        }
      }));
  };

  return (
    <Box marginTop="64px">
      <Box sx={userDetailsWrapper} marginBottom="24px">
        <PageHeading marginBottom="16px" color={blackOne} title={LABEL_SECURITY_CODE_PIN} subTitle={MSG_UPDATE_SECURITY_CODE} />

        <Button onClick={onButtonClick} variant="text" sx={{ border: `1px solid ${borderLight}`, textTransform: 'none' }} disabled={loading} endIcon={loading && <CircularProgress size={20} color="inherit" />}>
          {BUTTON_TEXT_CHANGE_PIN_CODE}
        </Button>
      </Box>

      <CustomModal maxWidth="600px" isOpen={isSecurityPinEdit} hideBackdrop>
        {modalStep2 ? (
          <Box>
            <Box display="flex" alignItems="center" padding="24px 0">
              <Box flex="1">
                <PageHeading marginBottom="0" color={blackOne} title={HEADING_SIX_DIGIT_PIN_CODE} />
              </Box>

              <IconButton onClick={onModalClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box borderBottom={`1px dashed ${blackFive}`} pb="16px">
              <CustomPinInput onChange={onChange} pinRef={pinRef} mb="0" />
            </Box>

            <Box alignItems="flex-end" display="flex" justifyContent="flex-end" mt="16px">
              <Button onClick={onTpinSubmit} variant="text" sx={{ border: `1px solid ${borderLight}`, textTransform: 'none' }}>
                {BUTTON_TEXT_CHANGE_PIN_CODE}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box padding="24px 0">
            <VerifyOtp isTpin displayPhoneNumber={phoneNumber!} infoSteps={infoSteps} sendOtp={sendOtpV2} handleNext={() => setModalStep2(true)} emailEditClose={onModalClose} code={code} />
          </Box>
        )}
      </CustomModal>

      {isSecurityPinEdit && <Box sx={backDropStyle} />}
    </Box>
  );
};
